import { useState } from "react";

import { differenceBy, isEmpty, toLower } from "lodash/fp";

import { Collapse, Typography } from "@mui/material";

import {
  RECOMMENDATION_CATEGORY_PROPS,
  Chip,
} from "@app/analytics/Recommendations/components/RecommendationCategory/styles";
import SeeAllButton from "@app/analytics/Recommendations/components/SeeAllButton";
import {
  PaginatorButton,
  RecommendationDetailContent,
  RecommendationDetailHeading,
} from "@app/analytics/Recommendations/styles";
import type { RecommendationCategoryType } from "@app/analytics/Recommendations/types";
import type { Recommendation as IRecommendation } from "@app/analytics/types";
import type { Pagination } from "@app/common/hooks/usePagination";
import { ELLIPSIS_SX } from "@app/common/styles";

import Stack, { Row } from "@rui/Stack";
import { StickyContainer } from "@rui/styles";

import Recommendation from "./components/Recommendation";

interface RecommendationCategoryDetailProps {
  category: RecommendationCategoryType;
  /** Recommendations for category filtered down to only those with
   * actions that match the applied filters */
  filteredRecommendations?: IRecommendation[];
  pagination: Pagination;
  /** All recommendations for the category */
  unfilteredRecommendations?: IRecommendation[];
}

const RecommendationCategoryDetail = ({
  category,
  filteredRecommendations = [],
  pagination,
  unfilteredRecommendations = [],
}: RecommendationCategoryDetailProps) => {
  const [showUnfilteredRecommendations, setShowUnfilteredRecommendations] =
    useState(false);

  // this panel will be collapsed when there are no recommendations to display
  if (isEmpty(filteredRecommendations)) return null;

  const { title, icon, color } = RECOMMENDATION_CATEGORY_PROPS[category];
  const otherRecommendations = differenceBy(
    "id",
    unfilteredRecommendations,
    filteredRecommendations
  );

  return (
    <Stack>
      <StickyContainer>
        <RecommendationDetailHeading variant="apart" spacing={2}>
          <PaginatorButton
            direction="back"
            onClick={pagination.goToPreviousPage}
            disabled={!pagination.hasPrevious}
          />
          <Row spacing={1} variant="center" sx={{ overflow: "hidden" }}>
            <Typography variant="h3" sx={ELLIPSIS_SX} title={title}>
              {title}
            </Typography>
            <Chip sx={{ bgcolor: color }}>
              {icon} <span>{unfilteredRecommendations.length}</span>
            </Chip>
          </Row>
          <PaginatorButton
            direction="forward"
            onClick={pagination.goToNextPage}
            disabled={!pagination.hasNext}
          />
        </RecommendationDetailHeading>
      </StickyContainer>
      <RecommendationDetailContent spacing={2}>
        {filteredRecommendations.map((rec) => (
          <Recommendation key={rec.id} recommendation={rec} />
        ))}
        {!isEmpty(otherRecommendations) && (
          <>
            <Collapse in={showUnfilteredRecommendations} unmountOnExit>
              <Stack spacing={2}>
                {otherRecommendations.map((rec) => (
                  <Recommendation key={rec.id} recommendation={rec} />
                ))}
              </Stack>
            </Collapse>
            <SeeAllButton
              expandLabel={`See all ${toLower(title)} recommendations`}
              expanded={showUnfilteredRecommendations}
              onCollapse={() => setShowUnfilteredRecommendations(false)}
              onExpand={() => setShowUnfilteredRecommendations(true)}
            />
          </>
        )}
      </RecommendationDetailContent>
    </Stack>
  );
};

export default RecommendationCategoryDetail;
